import {Injectable} from '@angular/core';
import {UserService} from './user.service';
import {AuthData} from '../../graphql/generated';
import {AuthService} from './auth.service';
import {ResourceService} from './resource.service';
import {DatabaseService} from './database.service';

import {
  ExtendedCategory,
  ExtendedMenu,
  ExtendedProduct,
  RouteChannelMessage,
  SyncAllMenu,
  TransFormedSyncAllMenu,
} from '../interfaces/interfaces';

import {OrderSyncService} from './order-sync.service';
import {IpService} from './ip.service';
import {NavigationEnd, Router} from '@angular/router';
import {Toast, ToastService} from './toast.service';
import {PrinterConfigService} from './printer-config.service';
import {ProgressBarService} from './progress-bar.service';
import {StorageService} from './storage.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {GoogleMapsLoaderService} from './google-maps-loader.service';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  private tabId: string;
  private broadcastChannel: BroadcastChannel;
  private routerSubscription: Subscription;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private resourceService: ResourceService,
    private databaseService: DatabaseService,
    private orderSync: OrderSyncService,
    private ipService: IpService,
    private router: Router,
    private toastService: ToastService,
    private printerConfigService: PrinterConfigService,
    private progressBarService: ProgressBarService,
    private storageService: StorageService,
    private googleMapsLoaderService: GoogleMapsLoaderService
  ) {
    this.broadcastChannel = new BroadcastChannel('route');
    this.tabId = Math.random().toString(36).substring(2);
    this.subscribeToRouteChanges();
  }

  getTabId() {
    return this.tabId;
  }

  async handleUserLogin(
    email: string,
    password: string,
    serverType: string = 'API_SERVER',
  ): Promise<void> {
    try {
      this.ipService.setServerType(serverType);
      const authData = await this.authService.authenticateUser(email, password);
      this.checkAuthData(authData);
      this.authService.saveAuthData(authData);
      await this.syncData();
      const isCallCenterAccount = this.checkCallCenterAccount();

      if (isCallCenterAccount) {
        localStorage.setItem('isCallCenter', 'true');
        await this.router.navigate(['main/call-center']);
        return;
      }

      localStorage.setItem('isCallCenter', 'false');
      await this.router.navigate(['main/branches']);
    } catch (error) {
      this.handleLoginError(error);
    }
  }

  private checkCallCenterAccount() {
    const user = this.userService.user();
    const permission = user.role?.permissions?.find((p: any) => p.permission_name === 'call_center');
    return !!permission?.read;
  }

  async handleUserLogout(): Promise<void> {
    await this.storageService.clearClientData();
    await this.router.navigate(['']);
  }

  async syncData() {
    const serverType = this.ipService.getServerType();
    this.progressBarService.setProgress(25, 'Syncing User Data', true).then();
    const user = await this.userService.getUserById();
    this.userService.setUser(user);

    this.progressBarService
      .setProgress(50, 'Syncing Configuration Data', true)
      .then();

    const syncAllData = await this.resourceService.syncAll(serverType);
    this.progressBarService.setProgress(75, 'Syncing Menu Data', true).then();
    const syncAllMenuData = await this.resourceService.syncAllMenu(serverType);
    const transformedSyncAllMenu = this.transformSyncAllMenuData(syncAllMenuData);
    await this.progressBarService.setProgress(100, 'Syncing Orders', true);

    await this.databaseService.clearAll();
    await this.databaseService.saveSyncAllData(syncAllData);
    await this.databaseService.saveSyncAllMenuData(transformedSyncAllMenu);
    await this.printerConfigService.handlePrinterConfig();
    await this.orderSync.handleOrderPolling();
    
    const cleanedCount = await this.databaseService.cleanupIncompleteDraftOrders();
    if (cleanedCount > 0) {
      console.log(`Cleaned up ${cleanedCount} incomplete draft orders`);
    }
    
    await this.setApiKeys();
    await this.setCurrency();
    this.progressBarService.setProgress(0, 'Synced', false).then();
  }

  async syncCloudData() {
    const serverType = this.ipService.getServerType();
    await this.resourceService.cloudSync(serverType);
  }

  handleLoginError(error: any): void {
    const toast = {
      className: 'tw-bg-red-500 text-light',
      header: 'Error',
      message: error.message,
    } as Toast;

    this.toastService.show(toast);
  }

  checkAuthData(authData: AuthData | undefined): void {
    const message = 'Authentication tokens not found.';

    if (authData == null) {
      throw new Error(message);
    } else if (
      authData?.access_token == null ||
      authData?.refresh_token == null ||
      authData?.id == null
    ) {
      throw new Error(message);
    }
  }

  transformSyncAllMenuData( syncAllMenuData: SyncAllMenu): TransFormedSyncAllMenu {
    const menuConfig = this.getMenuConfig(syncAllMenuData);
    const menus = this.getMenus(syncAllMenuData);
    const categories = this.getCategoriesFromMenus(syncAllMenuData);
    const products = this.getProductFromMenus(syncAllMenuData);

    return {
      menuConfig,
      menus,
      categories,
      products,
    } as TransFormedSyncAllMenu;
  }

  getMenuConfig(syncAllMenuData: SyncAllMenu) {
    return syncAllMenuData.menu_config?.map((menuConfig) => {
      return {
        ...menuConfig,
        default: menuConfig.default ? 1 : 0,
      };
    });
  }

  getMenus(syncAllMenuData: SyncAllMenu) {
    return syncAllMenuData.menus?.map((menu) => {
      return {
        menu_id: menu.menu_id,
      } as ExtendedMenu;
    });
  }

  getCategoriesFromMenus(syncAllMenuData: SyncAllMenu) {
    let categories: ExtendedCategory[] = [];

    for (const menu of syncAllMenuData.menus ?? []) {
      categories = categories.concat(
        menu.categories as unknown as ExtendedCategory,
      );
    }

    return categories;
  }

  getProductFromMenus(syncAllMenuData: SyncAllMenu) {
    let products: ExtendedProduct[] = [];

    for (const menu of syncAllMenuData.menus ?? []) {
      products = products.concat(menu.products as unknown as ExtendedProduct);
    }

    return products;
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    this.broadcastChannel.close();
  }

  private subscribeToRouteChanges() {
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.sendRouteChannelMessage(event.urlAfterRedirects);
      });
  }

  private sendRouteChannelMessage(url: string) {
    const tabId = this.tabId;
    const messageType = 'route_change';
    const message: RouteChannelMessage = { tabId, messageType, url };
    this.broadcastChannel.postMessage(message);
  }

  async setApiKeys() {
    const res = await this.databaseService.getIntegrationByName('Google Maps');
    if (!res || !res.settings.maps_key) {
      return;
    }
    await this.googleMapsLoaderService.loadGoogleMaps(res.settings.maps_key);
  }

  private async setCurrency() {
    const res = await this.databaseService.getSubCompany();
    if (!res || !res[0].currency) {
      return;
    }
    localStorage.setItem('currency', res[0].currency);
  }
}
